import { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";
import { FileText, Image as ImageIcon, Lock } from "lucide-react";
import moment from "moment";
import {
  getSharedDocuments,
  getSharedDocumentsErrorMessage,
} from "../../redux/api/Appoinment/SharedDocumentsApi";
import SharedDocumentViewer from "./SharedDocumentViewer";
import { cn } from "../lib/utill";

const TYPE_LABELS = {
  PRESCRIPTION: "Prescription",
  LAB_REPORT: "Lab Report",
  DISCHARGE_SUMMARY: "Discharge Summary",
  OTHER: "Other",
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Why a history entry can't be opened: removed by the client, or the access window has passed.
const unavailableLabel = (doc) => {
  if (doc.status === "DELETED") return "Deleted by client";
  if (doc.status === "UNSHARED") return "Removed by client";
  return "Access expired";
};

// Client-shared documents for an appointment; pass pollInterval (ms) to auto-refresh.
export default function SharedDocuments({ appointmentId, pollInterval, className }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiredMessage, setExpiredMessage] = useState(null);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);

  const loadDocuments = useCallback(async () => {
    if (!appointmentId) return;
    try {
      setDocuments(await getSharedDocuments(appointmentId));
      setExpiredMessage(null);
      setError(null);
    } catch (err) {
      const msg = await getSharedDocumentsErrorMessage(err);
      if (err?.response?.status === 403) {
        setDocuments([]);
        setExpiredMessage(msg || "Access to shared documents has expired");
      } else {
        setError(msg || "Unable to load shared documents.");
      }
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    setLoading(true);
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (!pollInterval || expiredMessage) return undefined;
    const interval = setInterval(loadDocuments, pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval, expiredMessage, loadDocuments]);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      );
    }
    if (expiredMessage) {
      return (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <Lock className="h-6 w-6 text-[#94A3B8]" />
          <p className="font-poppins text-sm font-medium text-[#171717] m-0">
            Access to shared documents has expired
          </p>
          {expiredMessage !== "Access to shared documents has expired" && (
            <p className="font-poppins text-xs text-[#334155] m-0">{expiredMessage}</p>
          )}
        </div>
      );
    }
    if (error && documents.length === 0) {
      return (
        <p className="font-poppins text-sm text-[#334155] text-center py-6 m-0">{error}</p>
      );
    }
    if (documents.length === 0) {
      return (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <FileText className="h-6 w-6 text-[#94A3B8]" />
          <p className="font-poppins text-sm text-[#334155] m-0">
            No documents shared by the client
          </p>
        </div>
      );
    }
    return (
      <ul className="flex flex-col gap-2 p-0 m-0 list-none">
        {documents.map((doc) => (
          <li
            key={doc.documentId}
            className="flex items-center justify-between gap-3 border border-[#E2E8F0] rounded-xl p-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 bg-[#EFF6FF] rounded-lg p-2">
                {doc.contentType === "application/pdf" ? (
                  <FileText className="h-5 w-5 text-[#4B61FF]" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-[#4B61FF]" />
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="font-poppins text-sm font-medium text-[#171717] truncate">
                  {doc.title}
                </span>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                  <span className="text-[#2563EB] text-xs font-semibold px-2 py-0.5 rounded-md bg-[#EFF6FF] font-poppins">
                    {TYPE_LABELS[doc.documentType] || doc.documentType}
                  </span>
                  <span className="font-poppins text-xs text-[#334155]">
                    {doc.viewable && formatSize(doc.sizeBytes)}
                    {doc.sharedOn &&
                      `${doc.viewable ? " · " : ""}Shared ${moment(doc.sharedOn).format("DD MMM YYYY, h:mm A")}`}
                  </span>
                </div>
              </div>
            </div>
            {doc.viewable ? (
              <button
                className="flex-shrink-0 bg-[#F8FAFC] border border-solid border-[#E2E8F0] rounded-lg px-3 py-1.5 font-poppins text-sm font-semibold text-[#4B61FF] hover:bg-[#E8F4FF] transition-colors"
                onClick={() => setViewing(doc)}
              >
                View
              </button>
            ) : (
              <span className="flex-shrink-0 font-poppins text-xs font-medium text-[#8A8A8A]">
                {unavailableLabel(doc)}
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={cn("bg-white border border-[#E2E8F0] rounded-2xl p-3 md:p-4", className)}>
      <h2 className="text-base md:text-[18px] font-semibold text-[#171717] font-poppins mb-1">
        Shared documents
      </h2>
      {documents.some((doc) => doc.viewable) && documents[0]?.accessExpiresOn && (
        <p className="font-poppins text-xs text-[#334155] m-0">
          Viewable until {moment(documents[0].accessExpiresOn).format("DD MMM YYYY, h:mm A")}
        </p>
      )}
      <div className="mb-3" />
      {renderBody()}
      <SharedDocumentViewer
        appointmentId={appointmentId}
        doc={viewing}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}
